import { useContext } from 'react';
import { AuthContext } from '../../providers/AuthProvider';
import { ApiMethod } from '../models/api-types.type';
import { routes } from '../../assets/constants';
import type { PhotoAnalysisJob } from '../../modules/vendor/edit-product-v3/api/productV2Api';

export interface DemoSite {
  slug: string;
  url: string;
  title: string;
  /** An uploaded site, or a link pointing at another page. */
  kind: 'site' | 'link';
  targetUrl: string;
  linkMode: LinkMode;
  injectSdk: boolean;
  fileCount: number;
  totalBytes: number;
  publishedAt: string;
  publishedBy: string;
  /** For a demo made by the generator: the product page it was copied from. */
  sourceUrl?: string;
  productSku?: string;
  vendor: { _id: string; name: string; alias: string } | null;
  /** The last quality check; 'pending' while one is due after a generate. */
  quality?: DemoQuality | null;
  /** When the demo comes down by itself (generated prospect demos); null keeps it. */
  expiresAt?: string | null;
  /** Calls booked from this demo's bar (ARDE-292): how many, and when the last was sent. */
  bookings?: { count: number; lastAt: string | null };
  /** When the click tracker was put in its pages; null for demos published before it existed. */
  tracking?: { at: string } | null;
  /** When someone outside the Ardealy team first opened it (the sales team was told then). */
  firstVisitAt?: string | null;
}

/** A demo's visitors at a glance (the Ardealy team's own visits left out). */
export interface DemoVisitSummary {
  views: number;
  visitors: number;
  clicks: number;
  /** Visualizer opens, room photos uploaded, products tried. */
  visualizer: number;
  roomUploads: number;
  productsTried: number;
  /** Shopping assistant opens and questions asked. */
  assistant: number;
  questions: number;
  /** "Book a demo" clicks, calls booked from its form, signup link clicks. */
  bookClicks: number;
  bookings: number;
  signupClicks: number;
  firstAt: string | null;
  lastAt: string | null;
  /** Team views left out. */
  team: number;
}

export type DemoClickKind = 'visualizer' | 'chat' | 'ar' | 'cart' | 'book' | 'signup' | 'link' | 'button';
/** What the visitor did inside our visualizer and assistant, and a call booked from the demo bar. */
export type DemoAction = 'visualizer_opened' | 'room_uploaded' | 'product_tried' | 'assistant_opened' | 'question_asked' | 'booking_sent';

export interface DemoVisitStats {
  slug: string;
  days: number;
  since: string;
  tracking: { at: string } | null;
  /** All time, not only this period. */
  firstVisitAt?: string | null;
  totals: {
    views: number;
    clicks: number;
    cart: number;
    visualizerClicks: number;
    bookClicks: number;
    signupClicks: number;
    visualizer: number;
    roomUploads: number;
    productsTried: number;
    assistant: number;
    questions: number;
    bookings: number;
    /** Within the period. */
    firstAt: string | null;
    lastAt: string | null;
    visitors: number;
    sessions: number;
    clickedSessions: number;
    returning: number;
    avgSeconds: number;
    avgScroll: number;
  };
  excluded: { views: number; clicks: number; people: string[] };
  clicks: { kind: DemoClickKind; label?: string; count: number; visitors: number; lastAt: string }[];
  /** The products tried in the visualizer, most tried first. */
  products?: { label: string; count: number; visitors: number; lastAt: string }[];
  /** The pages seen: the demo itself as "/", any other page of the site by its path ("/overview.html"). */
  pages?: { page: string; views: number; visitors: number; lastAt: string }[];
  daily: { day: string; views: number; clicks: number; visitors: number }[];
  devices: { device: 'phone' | 'tablet' | 'desktop'; visitors: number }[];
  referrers: { referrer: string; visitors: number }[];
  countries: { country: string; visitors: number }[];
  sessions: {
    session: string;
    visitor: string;
    startedAt: string;
    endedAt: string;
    device?: 'phone' | 'tablet' | 'desktop';
    country?: string;
    referrer?: string;
    views: number;
    /** The pages this visit saw, in order ("/" is the demo itself). */
    pages?: string[];
    durationMs?: number;
    scroll?: number;
    clicks: { kind: DemoClickKind; label?: string; at: string; page?: string }[];
    actions?: { action: DemoAction; label?: string; at: string; page?: string }[];
  }[];
}

export type QualityCheckId =
  | 'page' | 'visualizer' | 'gallery' | 'overlays' | 'phone' | 'assets'
  | 'logo' | 'colors' | 'language' | 'rooms' | 'areas';

export interface DemoQualityCheck {
  id: QualityCheckId | string;
  status: 'pass' | 'warn' | 'fail';
  /** What was found, in a sentence (from the API, in English). */
  message: string;
  /** What to do about it, when it is not a pass. */
  fix?: string;
}

export interface DemoQuality {
  status: 'pending' | 'ready' | 'review' | 'broken';
  score?: number;
  checks?: DemoQualityCheck[];
  checkedAt?: string;
  screenshots?: { desktop?: string; phone?: string };
}

export interface SlugStatus {
  slug: string;
  valid: boolean;
  problem: string | null;
  taken: boolean;
  takenBy: string | null;
  takenByVendorId: string | null;
  url: string | null;
  /** The vendor this name is the alias of, if any. The generator refuses a customer's. */
  vendorWithAlias?: { _id: string; name: string; kind: 'customer' | 'prospect' } | null;
}

/** What a demo site is published from: one zip, or a folder's files with their relative paths. */
export type DemoUpload =
  | { kind: 'zip'; file: File }
  | { kind: 'folder'; files: File[]; paths: string[] };

/** redirect: the visitor is sent on (address changes); embed: the page shows under the ardealy.link address. */
export type LinkMode = 'redirect' | 'embed';

export type PublishDemoSite = { slug: string; vendorId: string; title: string } & (
  | { kind: 'site'; injectSdk: boolean; upload: DemoUpload }
  | { kind: 'link'; targetUrl: string; linkMode: LinkMode }
);

/** A product in the Ardealy demo catalogue, as the demo generator offers it. */
export interface DemoProduct {
  uuid: string;
  id: string;
  name: string;
  sku: string;
  thumbnailPath: string;
  familySlug: string;
  link: string;
  isDraft: boolean;
  isVisible: boolean;
  variants: number;
  createdAt: string;
}

export type DemoVisualization = 'surface' | 'model3d' | 'image_only';

/** What the "Add product with AI" extractor read off a product page. */
export interface ScrapedDemoProduct {
  name: string;
  familySlug?: string | null;
  brand?: string;
  description?: string;
  sku?: string;
  color?: { value: string; hex?: string };
  size?: { width: number; height: number; depth?: number; unit: string };
  pattern?: string;
  productType?: string;
  applicationTypes?: string[];
  productPageUrl: string;
  imageUrls: string[];
  price?: string;
  /** Furniture category the scan suggests — `rugs` when the page is for a rug. */
  category?: string | null;
}

/** The vendor a demo is made for: one per demo, kept apart from customers until converted. */
export interface DemoProspect {
  _id: string | null;
  name: string | null;
  alias: string | null;
}

/** How the visualizer shows on the demo: a button that opens it, or the widget (the visualizer as a panel on the page). */
export type VisualizerSurface = 'button' | 'widget';

/** One place on the page the visualizer could go, as the scan found it. */
export interface PlacementSpot {
  /** `<surface>:<kind>`, e.g. button:add-to-cart. */
  id: string;
  surface: VisualizerSurface;
  kind: 'replace' | 'add-to-cart' | 'title' | 'gallery' | 'floating';
  /** A few words of the element it sits by, to tell spots apart. */
  text?: string;
  /** The one to choose when nobody chooses. */
  recommended: boolean;
}

export interface DemoPlacementOptions {
  /** Per surface, best first; none when the page could not be rendered. */
  spots: PlacementSpot[];
  /** A screenshot per surface with its spots numbered in list order (JPEG data URIs); absent where no browser rendered the page. */
  previews: { button?: string; widget?: string };
}

export interface DemoPlacement {
  surface: VisualizerSurface;
  spot?: string;
}

export interface DemoScan {
  /** The prospect this demo is for: made on generate, unless one already has its name. */
  prospect: DemoProspect & { exists: boolean };
  families: { _id: string; slug: string; name: string; visualization: DemoVisualization }[];
  product: ScrapedDemoProduct;
  /** The furniture categories the visualizer's markers use. */
  categories: { slug: string; displayName: string }[];
  /** Products already in the demo catalogue for this page. */
  existing: DemoProduct[];
  /** Where the visualizer could go on the page; absent from an API that predates it. */
  placement?: DemoPlacementOptions;
}

/** The part of a chosen photo to keep, in percent of the photo; the API cuts it out. */
export interface DemoImageCrop {
  url: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

/** A product to add to the demo catalogue. */
export interface NewDemoProduct {
  familySlug: string;
  name: string;
  sku?: string;
  brand?: string;
  description?: string;
  imageUrls: string[];
  /** Only for the photos that were cropped; the others are used whole. */
  imageCrops?: DemoImageCrop[];
  /** Furniture only. `rugs` makes the API build the rug's 3D model from its size and main photo. */
  category?: string;
  color?: ScrapedDemoProduct['color'];
  size?: ScrapedDemoProduct['size'];
  pattern?: string;
  productType?: string;
  applicationTypes?: string[];
  /** A surface's tiles (or seamless texture) cut from the photos by Build from photos, used in place of them. */
  tiles?: DemoTiles;
  /** A surface without tiles: the photos (indexes into imageUrls) laid as its texture; the others are its gallery. Default [0]. */
  planks?: number[];
}

export interface DemoTiles {
  mode: 'tiles' | 'seamless';
  urls: string[];
  pattern?: string;
  cellSizeCm?: number[];
  mirror?: boolean;
}

/** The photos Build from photos reads for a demo surface (crops applied by the API). */
export interface DemoTilesInput {
  imageUrls: string[];
  imageCrops?: DemoImageCrop[];
  size?: { width: number; height: number };
  pattern?: string;
  colorHex?: string;
}

export type GenerateDemo = {
  url: string;
  slug: string;
  placement?: DemoPlacement;
  /** The AI placement step (ARDE-287): a model places the visualizer on the captured page. Default true; a spot in `placement` is kept as chosen. */
  aiPlacement?: boolean;
  /** The brand named on the demo's private-demo bar; the page's own name when absent. */
  brandName?: string;
} & ({ productUuid: string } | { product: NewDemoProduct });

/** What the AI placement step did on a generated demo (also on the demo's record). */
export type AiPlacementStatus = 'applied' | 'partial' | 'fallback' | 'kept' | 'failed' | 'skipped' | 'off';

export interface GeneratedDemoPlacement {
  surface: VisualizerSurface | 'both';
  /** Where it went: a spot id, `button:ai-before`… for the AI's, joined with + for both surfaces. */
  spot: string;
  ai: {
    status: AiPlacementStatus;
    model?: string;
    reason?: string;
    problems?: string[];
    error?: string;
  };
}

export interface DemoProductStatus {
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  error?: string;
}

export interface GeneratedDemo {
  slug: string;
  url: string;
  /** The published files straight from storage; null when the API has no CDN configured. */
  previewUrl: string | null;
  fileCount: number;
  totalBytes: number;
  renderedWith: 'browser' | 'html';
  assetsSaved: number;
  assetsSkipped: number;
  product: DemoProduct;
  /** Set when the product was added now; its creation runs in the background. */
  batchId: string | null;
  /** Where the visualizer went and what the AI placement step did; absent on an API without it. */
  placement?: GeneratedDemoPlacement;
  /** The prospect the demo is for; `login` only when it was made now — its password is shown this once. */
  prospect: DemoProspect & { created: boolean; login?: { email: string; password: string } };
}

/** Super-admin publishing of vendor demo sites at <slug>.ardealy.link. */
/** Where a furniture model made by fal has got to. */
export interface FurnitureModelStatus {
  status: 'generating' | 'completed' | 'failed';
  error?: string;
  thumbnailUrl?: string;
}

export const useDemoSitesApi = () => {
  const { sendAuthGuardedRequest } = useContext(AuthContext);

  const list = (): Promise<DemoSite[]> =>
    sendAuthGuardedRequest(ApiMethod.GET, routes.demoSites.list) as Promise<DemoSite[]>;

  const slugStatus = (slug: string): Promise<SlugStatus> =>
    sendAuthGuardedRequest(ApiMethod.GET, `${routes.demoSites.slugStatus}?slug=${encodeURIComponent(slug)}`) as Promise<SlugStatus>;

  const publish = (input: PublishDemoSite): Promise<{ slug: string; url: string; fileCount: number }> => {
    const form = new FormData();
    form.append('slug', input.slug);
    form.append('vendorId', input.vendorId);
    form.append('title', input.title);
    form.append('kind', input.kind);
    if (input.kind === 'link') {
      form.append('targetUrl', input.targetUrl);
      form.append('linkMode', input.linkMode);
    } else {
      form.append('injectSdk', String(input.injectSdk));
      if (input.upload.kind === 'zip') {
        form.append('archive', input.upload.file);
      } else {
        // Multipart drops the folder part of a file name, so the paths travel alongside.
        input.upload.files.forEach((f) => form.append('files', f));
        form.append('paths', JSON.stringify(input.upload.paths));
      }
    }
    return sendAuthGuardedRequest(ApiMethod.POST, routes.demoSites.publish, form) as Promise<{ slug: string; url: string; fileCount: number }>;
  };

  /**
   * A rug's 3D model (.glb) from its top-down photo and its size in cm, made the way a
   * demo's rug is (border taken off, corners squared). The rug generator.
   */
  const rugModel = (image: File, widthCm: number, lengthCm: number, thicknessCm?: number): Promise<ArrayBuffer> => {
    const form = new FormData();
    form.append('image', image);
    form.append('widthCm', String(widthCm));
    form.append('lengthCm', String(lengthCm));
    if (thicknessCm !== undefined) form.append('thicknessCm', String(thicknessCm));
    return sendAuthGuardedRequest(ApiMethod.POST, routes.demoSites.rugModel, form, { responseType: 'arraybuffer' } as any) as Promise<ArrayBuffer>;
  };

  /** A piece of furniture's 3D model from its photo, made by fal (a minute or two): started, then polled. */
  const startFurnitureModel = (image: File): Promise<{ jobId: string }> => {
    const form = new FormData();
    form.append('image', image);
    return sendAuthGuardedRequest(ApiMethod.POST, routes.demoSites.furnitureModel, form) as Promise<{ jobId: string }>;
  };

  const furnitureModelStatus = (jobId: string): Promise<FurnitureModelStatus> =>
    sendAuthGuardedRequest(ApiMethod.GET, routes.demoSites.furnitureModelStatus(jobId)) as Promise<FurnitureModelStatus>;

  const furnitureModelGlb = (jobId: string): Promise<ArrayBuffer> =>
    sendAuthGuardedRequest(ApiMethod.GET, routes.demoSites.furnitureModelGlb(jobId), undefined, { responseType: 'arraybuffer' } as any) as Promise<ArrayBuffer>;

  /** Check a demo now: it is opened on a desktop and a phone (about a minute). */
  const checkQuality = (slug: string): Promise<DemoQuality> =>
    sendAuthGuardedRequest(ApiMethod.POST, routes.demoSites.quality(slug)) as Promise<DemoQuality>;

  const unpublish = (slug: string): Promise<{ slug: string; removed: number }> =>
    sendAuthGuardedRequest(ApiMethod.DELETE, routes.demoSites.unpublish(slug)) as Promise<{ slug: string; removed: number }>;

  /** Read a product page with the "Add product with AI" extractor, as the demo vendor. */
  const scanProductPage = (url: string, slug?: string): Promise<DemoScan> =>
    sendAuthGuardedRequest(ApiMethod.POST, routes.demoSites.generatorScan, { url, slug }) as Promise<DemoScan>;

  /** Add the product (or use one already in the catalogue), copy the page and publish <slug>.ardealy.link. */
  const generateDemo = (input: GenerateDemo): Promise<GeneratedDemo> =>
    sendAuthGuardedRequest(ApiMethod.POST, routes.demoSites.generate, input) as Promise<GeneratedDemo>;

  const demoProductStatus = (batchId: string, vendorId?: string | null): Promise<DemoProductStatus> =>
    sendAuthGuardedRequest(
      ApiMethod.GET,
      routes.demoSites.generatorProductStatus(batchId) + (vendorId ? `?vendor=${encodeURIComponent(vendorId)}` : ''),
    ) as Promise<DemoProductStatus>;

  /** Build from photos on a demo surface's photos (not charged). Poll with demoTilesStatus. */
  const startDemoTiles = (input: DemoTilesInput): Promise<PhotoAnalysisJob & { jobId: string }> =>
    sendAuthGuardedRequest(ApiMethod.POST, routes.demoSites.generatorTiles, input) as Promise<PhotoAnalysisJob>;

  const demoTilesStatus = (jobId: string): Promise<PhotoAnalysisJob> =>
    sendAuthGuardedRequest(ApiMethod.GET, `${routes.demoSites.generatorTiles}/${encodeURIComponent(jobId)}`) as Promise<PhotoAnalysisJob>;

  /** Keep a demo `days` more days from now, or for good (null). */
  const setExpiry = (slug: string, days: number | null): Promise<{ slug: string; expiresAt: string | null }> =>
    sendAuthGuardedRequest(ApiMethod.POST, routes.demoSites.expiry(slug), { days }) as Promise<{ slug: string; expiresAt: string | null }>;

  /** Every demo's visitors, clicks and last visit — or only one vendor's demos (its CRM lead). */
  const visitSummary = (vendorId?: string): Promise<Record<string, DemoVisitSummary>> =>
    sendAuthGuardedRequest(
      ApiMethod.GET,
      vendorId ? `${routes.demoSites.visitSummary}?vendor=${encodeURIComponent(vendorId)}` : routes.demoSites.visitSummary,
    ) as Promise<Record<string, DemoVisitSummary>>;

  /** One demo's visits over `days` days. */
  const visits = (slug: string, days: number): Promise<DemoVisitStats> =>
    sendAuthGuardedRequest(ApiMethod.GET, `${routes.demoSites.visits(slug)}?days=${days}`) as Promise<DemoVisitStats>;

  /** Put the click tracker in a demo published before it existed. */
  const enableTracking = (slug: string): Promise<{ slug: string; pages: number; updated: number }> =>
    sendAuthGuardedRequest(ApiMethod.POST, routes.demoSites.tracking(slug)) as Promise<{ slug: string; pages: number; updated: number }>;

  /** Put the current tracker (and analytics tag) in every published demo. */
  const enableTrackingEverywhere = (): Promise<{ sites: number; updated: number; failed: string[] }> =>
    sendAuthGuardedRequest(ApiMethod.POST, routes.demoSites.trackingEverywhere) as Promise<{ sites: number; updated: number; failed: string[] }>;

  return { list, slugStatus, publish, unpublish, checkQuality, setExpiry, visitSummary, visits, enableTracking, enableTrackingEverywhere, rugModel, startFurnitureModel, furnitureModelStatus, furnitureModelGlb, scanProductPage, generateDemo, demoProductStatus, startDemoTiles, demoTilesStatus };
};

/**
 * The API's own message from a failed request, or null. sendAuthGuardedRequest
 * hands back an Error already carrying it (with `status` and the parsed `body`);
 * a raw Response is still read for callers that bypass it.
 */
export const apiErrorMessage = async (e: unknown): Promise<string | null> => {
  if (e instanceof Error && typeof (e as any).status === 'number') {
    return e.message || null;
  }
  if (e && typeof (e as Response).json === 'function') {
    try {
      const body = await (e as Response).json();
      const message = Array.isArray(body?.message) ? body.message.join(', ') : body?.message;
      return typeof message === 'string' && message ? message : null;
    } catch {
      return null;
    }
  }
  return null;
};
